import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
  LabelList,
  ComposedChart,
} from 'recharts';
import { useMemo, useState } from 'react';
import type { DashboardChartConfig } from '@pm-next/shared-types';
import { humanizeLabel } from '@pm-next/shared-types';
import { resolveChartColors } from '@/components/ai/dashboards/chart-colors';

type ChartType = 'bar' | 'pie' | 'donut' | 'line' | 'area' | 'stack';

/** Single-series: one value per category. */
export interface ChartBlockSingleSeries {
  type: ChartType;
  title?: string;
  data: Array<{ name?: string; label?: string; value: number; formattedValue?: string }>;
}

/** Multi-series: multiple value keys per category (bar/line/area/stack only). */
export interface ChartBlockMultiSeries {
  type: 'bar' | 'line' | 'area' | 'stack';
  title?: string;
  series: string[];
  seriesLabels?: string[];
  data: Array<Record<string, string | number>>;
}

export type ChartBlockFormat = ChartBlockSingleSeries | ChartBlockMultiSeries;

function isMultiSeries(
  parsed: ChartBlockSingleSeries | ChartBlockMultiSeries
): parsed is ChartBlockMultiSeries {
  return (
    Array.isArray((parsed as ChartBlockMultiSeries).series) &&
    (parsed as ChartBlockMultiSeries).series.length > 0
  );
}

function normalizePayload(
  parsed: ChartBlockSingleSeries | ChartBlockMultiSeries
): ChartBlockSingleSeries | ChartBlockMultiSeries | null {
  if (!parsed?.data || !Array.isArray(parsed.data) || parsed.data.length === 0) return null;
  if (!['bar', 'pie', 'donut', 'line', 'area', 'stack'].includes(parsed.type)) return null;

  if (isMultiSeries(parsed)) {
    const series = parsed.series.filter((s) => typeof s === 'string' && s.length > 0);
    if (series.length === 0) return null;
    const data = parsed.data
      .filter((row) => row != null && typeof row === 'object' && ('name' in row || 'label' in row))
      .map((row) => ({
        ...row,
        name: row.name || row.label,
      }));
    if (data.length === 0) return null;
    return {
      type: parsed.type,
      title: parsed.title,
      series,
      seriesLabels: Array.isArray(parsed.seriesLabels) ? parsed.seriesLabels : undefined,
      data,
    } as ChartBlockMultiSeries;
  }

  const valid = (parsed as ChartBlockSingleSeries).data
    .filter(
      (d) =>
        d != null &&
        (typeof d.name === 'string' || typeof d.label === 'string') &&
        typeof d.value === 'number'
    )
    .map((d) => ({
      name: d.name || d.label,
      value: d.value,
      ...(typeof d.formattedValue === 'string' ? { formattedValue: d.formattedValue } : {}),
    }));
  if (valid.length === 0) return null;
  return { type: parsed.type, title: parsed.title, data: valid };
}

export interface InlineChartProps {
  /** Raw JSON string from AI ```chart code block (single or multi-series format) */
  code: string;
  className?: string;
  /** When false, omit the chart title inside the widget (default true). */
  showTitle?: boolean;
  /** Tailwind height class for the chart canvas (default h-[240px]). */
  chartHeightClassName?: string;
  /** When true, remove the bordered card chrome around the chart. */
  bare?: boolean;
  /** Dashboard / block chart options (color scheme, labels, legend). */
  chartConfig?: DashboardChartConfig | null;
}

function seriesKeyFromEvent(entry: unknown): string | null {
  if (entry == null || typeof entry !== 'object') return null;
  const row = entry as Record<string, unknown>;
  if (typeof row.dataKey === 'string' && row.dataKey) return row.dataKey;
  if (typeof row.value === 'string' && row.value) return row.value;
  return null;
}

/** Tick increment that keeps axis bounds on readable numbers. */
function niceStep(span: number): number {
  if (!(span > 0)) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(span));
  const normalized = span / magnitude;
  const step = normalized <= 1.5 ? 0.2 : normalized <= 3 ? 0.5 : normalized <= 7 ? 1 : 2;
  return step * magnitude;
}

function roundToStep(value: number, step: number, mode: 'floor' | 'ceil'): number {
  const scaled = value / step;
  const bounded = mode === 'floor' ? Math.floor(scaled) : Math.ceil(scaled);
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  return Number((bounded * step).toFixed(decimals));
}

/**
 * Zoom the value axis to the data range instead of always anchoring at 0, so
 * small variations aren't flattened into a thin band of a mostly empty chart.
 * The 0 baseline is kept when the data already sits close to it.
 */
export function computeValueDomain(values: number[]): [number, number] | undefined {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return undefined;

  const min = Math.min(...finite);
  const max = Math.max(...finite);

  if (min === max) {
    if (min === 0) return [0, 1];
    const pad = Math.abs(min) * 0.1;
    const step = niceStep(pad * 2);
    return [roundToStep(min - pad, step, 'floor'), roundToStep(max + pad, step, 'ceil')];
  }

  const span = max - min;
  const step = niceStep(span);
  // Anchor at 0 while the smallest value is still a small fraction of the largest;
  // beyond that the zero baseline is mostly empty space, so zoom to the data.
  const keepZeroBaseline = min >= 0 && min <= max * 0.35;

  return [
    keepZeroBaseline ? 0 : roundToStep(min - span * 0.1, step, 'floor'),
    roundToStep(max + span * 0.1, step, 'ceil'),
  ];
}

/** Renders a chart from AI-provided ```chart code block content. */
export function InlineChart({
  code,
  className = '',
  showTitle = true,
  chartHeightClassName = 'h-[240px]',
  bare = false,
  chartConfig = null,
}: InlineChartProps) {
  const payload = useMemo((): ChartBlockFormat | null => {
    try {
      const parsed = JSON.parse(code) as ChartBlockSingleSeries | ChartBlockMultiSeries;
      return normalizePayload(parsed);
    } catch {
      return null;
    }
  }, [code]);

  const colors = useMemo(
    () => resolveChartColors(chartConfig?.colorScheme),
    [chartConfig?.colorScheme]
  );

  // Local, non-persisted legend toggle: clicking a legend entry hides/shows it
  // in this chart only. Never written back to block filters.
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const toggleHidden = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const showLabels = chartConfig?.showLabels === true;
  const showLegend = chartConfig?.showLegend !== false;

  const seriesLegend = showLegend ? (
    <Legend
      wrapperStyle={{ fontSize: 11, cursor: 'pointer' }}
      onClick={(entry: unknown) => {
        const key = seriesKeyFromEvent(entry);
        if (key) toggleHidden(key);
      }}
      formatter={(value: unknown, entry: unknown) => {
        const key = seriesKeyFromEvent(entry);
        const isHidden = key != null && hidden.has(key);
        return (
          <span style={{ color: isHidden ? 'hsl(var(--muted-foreground))' : undefined }}>
            {String(value)}
          </span>
        );
      }}
    />
  ) : null;

  if (!payload) return null;

  const tooltipStyle = {
    fontSize: 12,
    borderRadius: 8,
    border: '1px solid hsl(var(--border))',
  } as const;

  const formatTooltipValue = (value: unknown, _name: unknown, item: unknown) => {
    const tipPayload = (item as { payload?: { formattedValue?: string } } | undefined)?.payload;
    if (tipPayload?.formattedValue) return tipPayload.formattedValue;
    return value as string | number;
  };

  if (isMultiSeries(payload)) {
    const seriesName = (key: string, i: number) => payload.seriesLabels?.[i] || humanizeLabel(key);
    const visibleSeries = payload.series.filter((key) => !hidden.has(key));
    // Stacked charts need the sum of visible series; side-by-side/line zoom to each value.
    const valueDomain =
      payload.type === 'stack'
        ? computeValueDomain(
            payload.data.map((row) =>
              visibleSeries.reduce(
                (sum, key) => sum + (typeof row[key] === 'number' ? (row[key] as number) : 0),
                0
              )
            )
          )
        : computeValueDomain(
            payload.data.flatMap((row) =>
              visibleSeries.map((key) =>
                typeof row[key] === 'number' ? (row[key] as number) : NaN
              )
            )
          );

    const renderMultiSeriesChart = () => {
      if (payload.type === 'area') {
        return (
          <AreaChart data={payload.data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
              domain={valueDomain}
            />
            <Tooltip contentStyle={tooltipStyle} />
            {seriesLegend}
            {payload.series.map((key, i) => (
              <Area
                key={key}
                dataKey={key}
                hide={hidden.has(key)}
                fill={colors[i % colors.length]}
                stroke={colors[i % colors.length]}
                fillOpacity={0.3}
                name={seriesName(key, i)}
              />
            ))}
          </AreaChart>
        );
      }
      if (payload.type === 'line') {
        return (
          <LineChart data={payload.data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
              domain={valueDomain}
            />
            <Tooltip contentStyle={tooltipStyle} />
            {seriesLegend}
            {payload.series.map((key, i) => (
              <Line
                key={key}
                dataKey={key}
                hide={hidden.has(key)}
                stroke={colors[i % colors.length]}
                strokeWidth={2}
                name={seriesName(key, i)}
              />
            ))}
          </LineChart>
        );
      }
      const stacked = payload.type === 'stack';
      const showTotalLine = stacked && chartConfig?.showTotalLine === true;
      const TOTAL_KEY = '__total';
      const configuredNegative = new Set(chartConfig?.negativeSeriesKeys ?? []);
      const isNegativeSeries = (key: string) => {
        if (configuredNegative.has(key)) return true;
        // Fallback when config wasn't passed: series already inverted by the API.
        const vals = payload.data
          .map((row) => row[key])
          .filter((v): v is number => typeof v === 'number' && v !== 0);
        return vals.length > 0 && vals.every((v) => v < 0);
      };
      const stackData = showTotalLine
        ? payload.data.map((row) => {
            const total = payload.series
              .filter((key) => !hidden.has(key))
              .reduce(
                (sum, key) => sum + (typeof row[key] === 'number' ? (row[key] as number) : 0),
                0
              );
            return { ...row, [TOTAL_KEY]: total };
          })
        : payload.data;
      const ChartSurface = showTotalLine ? ComposedChart : BarChart;
      // Draw positives first, then negatives, so the stack builds from zero in each direction.
      const orderedSeries = stacked
        ? [
            ...payload.series.filter((key) => !isNegativeSeries(key)),
            ...payload.series.filter((key) => isNegativeSeries(key)),
          ]
        : payload.series;
      return (
        <ChartSurface
          data={stackData}
          margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          // Separates +/− into stacks above/below zero on the same x slot.
          {...(stacked ? { stackOffset: 'sign' as const } : {})}
        >
          <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          {/* Bars/stacks stay anchored at 0 — truncating misrepresents magnitudes. */}
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
          <Tooltip contentStyle={tooltipStyle} />
          {seriesLegend}
          {orderedSeries.map((key) => {
            const i = payload.series.indexOf(key);
            const negative = isNegativeSeries(key);
            return (
              <Bar
                key={key}
                dataKey={key}
                hide={hidden.has(key)}
                fill={colors[i % colors.length]}
                radius={stacked ? [0, 0, 0, 0] : [4, 4, 0, 0]}
                stackId={stacked ? 'stack' : undefined}
                name={seriesName(key, i)}
              >
                {showLabels && (
                  <LabelList dataKey={key} position={negative ? 'bottom' : 'top'} fontSize={10} />
                )}
              </Bar>
            );
          })}
          {showTotalLine && (
            <Line
              type="monotone"
              dataKey={TOTAL_KEY}
              name="Total"
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              dot={{ r: 3 }}
              hide={hidden.has(TOTAL_KEY)}
            />
          )}
        </ChartSurface>
      );
    };

    return (
      <div
        className={`${bare ? '' : 'mt-3 rounded-lg border border-border/60 bg-muted/20 p-3'} ${className}`}
      >
        {showTitle && payload.title && (
          <div className="mb-2 text-sm font-medium text-foreground">{payload.title}</div>
        )}
        <div className={`${chartHeightClassName} w-full min-w-[280px]`}>
          <ResponsiveContainer width="100%" height="100%">
            {renderMultiSeriesChart()}
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  const renderChart = () => {
    const singlePayload = payload as ChartBlockSingleSeries;
    // Keep a stable color per category, then drop locally-hidden categories.
    const colored = singlePayload.data.map((d, i) => ({
      ...d,
      __color: colors[i % colors.length],
    }));
    const visibleData = colored.filter((d) => !hidden.has(String(d.name ?? '')));
    const valueDomain = computeValueDomain(visibleData.map((d) => d.value));
    const categoryLegend = showLegend ? (
      <Legend
        wrapperStyle={{ fontSize: 11 }}
        content={() => (
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 pt-1 text-[11px] text-foreground">
            {colored.map((d, i) => {
              const name = String(d.name ?? '');
              const isHidden = hidden.has(name);
              return (
                <button
                  key={`${name}-${i}`}
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1.5 hover:underline"
                  style={{ opacity: isHidden ? 0.4 : 1 }}
                  onClick={() => toggleHidden(name)}
                >
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: d.__color }}
                    aria-hidden
                  />
                  <span style={{ textDecoration: isHidden ? 'line-through' : undefined }}>
                    {name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      />
    ) : null;

    if (singlePayload.type === 'bar' || singlePayload.type === 'stack') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={visibleData} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            {/* Bars stay anchored at 0 — truncating a bar axis misrepresents magnitudes. */}
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
            <Tooltip contentStyle={tooltipStyle} formatter={formatTooltipValue} />
            {categoryLegend}
            <Bar dataKey="value" radius={[4, 4, 0, 0]} name="Value">
              {visibleData.map((d, i) => (
                <Cell key={i} fill={d.__color} />
              ))}
              {showLabels && (
                <LabelList
                  dataKey="formattedValue"
                  position="top"
                  fontSize={10}
                  formatter={(v: unknown) => (v == null || v === '' ? undefined : String(v))}
                />
              )}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (singlePayload.type === 'line') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={visibleData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
              domain={valueDomain}
            />
            <Tooltip contentStyle={tooltipStyle} formatter={formatTooltipValue} />
            {categoryLegend}
            <Line dataKey="value" stroke={colors[0]} strokeWidth={2} dot={{ r: 4 }} name="Value">
              {showLabels && <LabelList dataKey="formattedValue" position="top" fontSize={10} />}
            </Line>
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (singlePayload.type === 'area') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={visibleData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
              domain={valueDomain}
            />
            <Tooltip contentStyle={tooltipStyle} formatter={formatTooltipValue} />
            {categoryLegend}
            <Area
              dataKey="value"
              stroke={colors[0]}
              fill={colors[0]}
              fillOpacity={0.3}
              name="Value"
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    const isDonut = singlePayload.type === 'donut';
    const pieLabel =
      showLabels || !bare
        ? ({
            name,
            value,
            payload: slice,
          }: {
            name?: string;
            value?: number;
            payload?: { formattedValue?: string };
          }) => `${name}: ${slice?.formattedValue ?? value}`
        : false;

    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <Pie
            data={visibleData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={isDonut ? '45%' : 0}
            outerRadius={bare ? '65%' : '80%'}
            label={pieLabel}
          >
            {visibleData.map((d, i) => (
              <Cell key={i} fill={d.__color} />
            ))}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} formatter={formatTooltipValue} />
          {categoryLegend}
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const singlePayload = payload as ChartBlockSingleSeries;

  return (
    <div
      className={`${bare ? '' : 'mt-3 rounded-lg border border-border/60 bg-muted/20 p-3'} ${className}`}
    >
      {showTitle && singlePayload.title && (
        <div className="mb-2 text-sm font-medium text-foreground">{singlePayload.title}</div>
      )}
      <div className={`${chartHeightClassName} w-full min-w-[280px]`}>{renderChart()}</div>
    </div>
  );
}
